/**
 * Мобильный экран подтверждения (M6, задача 71 §6.2) — ветка «Подтверждение»
 * хаба `/field/lead`. Переиспользует то же тело `ApprovalConsole`, что десктоп
 * D6, но в мобильной рамке (одна колонка, top-bar с «Назад» в хаб).
 *
 * «3 экрана» макапа (Дом / Не сдали / Внести) достигаются переходами-оверлеями
 * внутри консоли: раскрытая `MissingPanel` = «Не сдали», `EntryDock`-`Sheet` на
 * узком вьюпорте перекрывает экран = «Внести» (не док справа). Это сознательный
 * порт (см. `PORT NOTE` макапа): логика и данные те же, что D6.
 *
 * `openMyTime` (search `?mytime=1`) — ветка «Моё время»: открывает self-entry
 * мастера (актор==субъект → approved §1.1/§3.1) поверх той же консоли, БЕЗ
 * worker-`/field` (он под `WorkerOnly`, §6.1-ловушка).
 */
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "@tanstack/react-router";
import { ChevronLeft } from "lucide-react";

import { ApprovalConsole, type ApprovalConsoleHandle } from "@/components/approval/ApprovalConsole";
import { ApprovalUnitPicker, ApprovalUnitSelectionSync } from "@/components/approval/UnitPicker";
import {
  approvalUnitsForMode,
  approvalUnitsInScope,
  resolveApprovalUnitSelection,
} from "@/components/approval/approvalUnitSelection";
import { useUnits } from "@/hooks/useUnits";
import { useSession } from "@/store/session";

interface LeadApprovalPageProps {
  weekStart: string;
  selectedDate: string;
  selectedUnitId: number | null;
  openMyTime: boolean;
  onWeekStartChange: (weekStart: string) => void;
  onSelectedDateChange: (date: string) => void;
  onUnitChange: (unitId: number | null) => void;
}

export function LeadApprovalPage({
  weekStart,
  selectedDate,
  selectedUnitId,
  openMyTime,
  onWeekStartChange,
  onSelectedDateChange,
  onUnitChange,
}: LeadApprovalPageProps): React.ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const consoleRef = React.useRef<ApprovalConsoleHandle>(null);
  const role = useSession((state) => state.user?.role);
  const actingUnitIds = useSession((state) => state.user?.acting_unit_ids);
  const unitsQuery = useUnits({ refetchOnMount: "always" });
  const scopedUnits = React.useMemo(
    () => approvalUnitsInScope(unitsQuery.data ?? [], role, actingUnitIds),
    [unitsQuery.data, role, actingUnitIds],
  );
  const dailyUnits = React.useMemo(() => approvalUnitsForMode(scopedUnits, "daily"), [scopedUnits]);
  const selection = resolveApprovalUnitSelection({
    units: dailyUnits,
    role,
    selectedUnitId,
    ready: role !== undefined && unitsQuery.isFetchedAfterMount,
  });

  // «Моё время» из хаба: открыть self-entry мастера один раз на mount.
  const openedMyTime = React.useRef(false);
  React.useEffect(() => {
    if (openMyTime && !openedMyTime.current) {
      openedMyTime.current = true;
      consoleRef.current?.openMyTime();
    }
  }, [openMyTime]);

  return (
    <div className="flex min-h-screen flex-col bg-[var(--bg-panel-1)]">
      <header className="sticky top-0 z-10 flex h-12 items-center gap-2 border-b border-[var(--rule-line)] bg-[var(--bg-panel-0)] px-3">
        <button
          type="button"
          data-testid="lead-approval-back"
          aria-label={t("leadHub.back")}
          onClick={() => void navigate({ to: "/field/lead" })}
          className="inline-flex items-center gap-0.5 text-sm text-[var(--fg-2)]"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden />
          {t("leadHub.back")}
        </button>
        <span className="flex-1" />
        <span className="text-sm font-semibold text-[var(--fg-1)]">
          {t("leadHub.approvalScreenTitle")}
        </span>
      </header>

      <main className="flex flex-1 flex-col gap-4 p-3" data-testid="lead-approval">
        <ApprovalUnitSelectionSync
          selectedUnitId={selectedUnitId}
          nextUnitId={selection.nextUrlUnitId}
          onUnitChange={onUnitChange}
        />
        <ApprovalUnitPicker
          visible={selection.showPicker}
          units={dailyUnits}
          selectedUnitId={selection.effectiveUnitId}
          onChange={onUnitChange}
        />
        <ApprovalConsole
          ref={consoleRef}
          density="mobile"
          weekStart={weekStart}
          selectedDate={selectedDate}
          selectedUnitId={selection.effectiveUnitId}
          queriesEnabled={selection.queriesEnabled}
          needsUnit={selection.needsUnit}
          onWeekStartChange={onWeekStartChange}
          onSelectedDateChange={onSelectedDateChange}
        />
      </main>
    </div>
  );
}
